import { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { Table, useApiQuery, type Column } from 'grabbitbot-legacy-ui'
import { wnfClient } from '../../api'
import type { Wallet } from '../../api/types'
import { PageState } from '../../components/PageState'
import { TestFundsBanner } from '../../components/TestFundsBanner'
import { describeLoadError } from '../../lib/errors'
import { money, numericSortKey, utcDateTime } from '../../lib/format'
import { useDepositableCoins } from '../../lib/useDepositableCoins'

const columns: Column<Wallet>[] = [
  {
    key: 'coin',
    label: 'Wallet',
    align: 'left',
    format: (_v, row) => <Link to={`/wallets/${row.coin}`}>{row.coin}</Link>,
  },
  {
    key: 'balance',
    label: 'Balance (test funds)',
    numeric: true,
    cellClass: () => 'wnf-num',
    format: (v) => money(v as string | number),
    sortValue: (row) => numericSortKey(row.balance),
  },
  { key: 'lastModAt', label: 'Last mod at', format: (v) => utcDateTime(v as string | null) },
]

/** "Deposit test funds": pick any depositable coin and open its wallet sheet —
 * works for coins the user has no wallet of yet. */
function DepositLauncher() {
  const navigate = useNavigate()
  const { coins, loading, error } = useDepositableCoins()
  const [coin, setCoin] = useState('')
  const selected = coin || coins[0]?.coin || ''
  const min = coins.find((c) => c.coin === selected)?.minAmount
  return (
    <form
      className="wnf-card wnf-launcher"
      aria-label="Deposit test funds"
      data-testid="deposit-launcher"
      onSubmit={(e) => {
        e.preventDefault()
        if (selected) void navigate(`/wallets/${selected}`)
      }}
    >
      <h2>Deposit test funds</h2>
      <div className="wnf-launcher__row">
        <label>
          Coin
          <select value={selected} onChange={(e) => setCoin(e.target.value)} disabled={loading || coins.length === 0}>
            {coins.map((c) => (
              <option key={c.coin} value={c.coin}>
                {c.coin}
              </option>
            ))}
          </select>
        </label>
        <button type="submit" disabled={!selected}>
          Open deposit
        </button>
      </div>
      {min && <p className="wnf-muted">Minimum for {selected}: {min}. Any coin listed here can be deposited, even without a wallet yet.</p>}
      {error && <p className="wnf-muted">Could not load the list of depositable coins.</p>}
    </form>
  )
}

/** Legacy `WalletListComponent` (`:tradingType/wallets`). */
export function WalletListPage() {
  const { data, loading, error } = useApiQuery(() => wnfClient.searchWallets(), [])
  return (
    <div>
      <div className="wnf-page-title">
        <h1>Wallets — spot</h1>
      </div>
      <TestFundsBanner />
      <DepositLauncher />
      <PageState loading={loading} error={error ? describeLoadError(error, 'wallets') : null}>
        {data && data.length === 0 && <p className="wnf-muted">No wallets yet — open a wallet and top up test funds.</p>}
        {data && data.length > 0 && (
          <Table columns={columns} rows={data} rowKey={(row) => row.coin} initialSort={{ key: 'coin', dir: 'asc' }} />
        )}
      </PageState>
    </div>
  )
}
